import { useEffect, useState } from 'react';
import type { ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categoryList } from '@/mocks/categories';
import { articles } from '@/mocks/articles';
import { productCategories } from '@/mocks/products';

const heroBackground = 'https://readdy.ai/api/search-image?query=Abstract%20soft%20neutral%20gradient%20background%20with%20floating%20fashion%20product%20silhouettes%2C%20warm%20cream%20and%20beige%20tones%2C%20minimal%20clean%20aesthetic%2C%20subtle%20texture%2C%20editorial%20style%2C%20gentle%20shadows%2C%20premium%20modern%20design%2C%20suitable%20for%20website%20hero%20section&width=1600&height=900&seq=hero-bg-001&orientation=landscape';

export default function Home() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [selectedCategory, setSelectedCategory] = useState('');
  const [scrollY, setScrollY] = useState(0);

  // Handle language from URL
  useEffect(() => {
    const path = location.pathname;
    if (path.startsWith('/pt')) {
      i18n.changeLanguage('pt');
    } else if (path.startsWith('/de')) {
      i18n.changeLanguage('de');
    } else {
      i18n.changeLanguage('en');
    }
  }, [location.pathname, i18n]);

  // Scroll listener for parallax
  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleCategorySelect = (e: ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    if (value) {
      window.open(value, '_blank', 'noopener,noreferrer');
    }
    setSelectedCategory(value);
  };

  const langCode = i18n.language || 'en';

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={heroBackground}
            alt="Background"
            className="w-full h-full object-cover"
            style={{ transform: `translateY(${scrollY * 0.3}px)` }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50" />
        </div>

        <div className="relative z-10 w-full px-4 md:px-6 lg:px-8 text-center max-w-5xl mx-auto pt-20">
          <span className="inline-block px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-sm text-white/90 text-sm font-medium tracking-wide mb-6 animate-fade-in">
            {t('hero.eyebrow')}
          </span>
          <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-white leading-tight mb-6 animate-slide-up">
            {t('hero.h1')}
          </h1>
          <p className="text-base md:text-lg lg:text-xl text-white/85 max-w-3xl mx-auto mb-8 leading-relaxed animate-slide-up" style={{ animationDelay: '0.15s' }}>
            {t('hero.subtitle')}
          </p>

          <div className="flex flex-wrap justify-center gap-3 mb-10 animate-fade-in" style={{ animationDelay: '0.3s' }}>
            {['trust.tag1', 'trust.tag2', 'trust.tag3', 'trust.tag4'].map((tag) => (
              <span key={tag} className="px-3 py-1 rounded-full bg-white/10 backdrop-blur-sm text-white/80 text-xs font-medium">
                {t(tag)}
              </span>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12 animate-fade-in" style={{ animationDelay: '0.4s' }}>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 py-4 bg-primary-500 hover:bg-primary-600 text-white font-semibold rounded-lg transition-all duration-200 text-base whitespace-nowrap shadow-lg hover:shadow-xl"
            >
              {t('cta.cssbuySpreadsheet')}
              <i className="ri-external-link-line w-4 h-4 flex items-center justify-center" />
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 py-4 bg-white/15 hover:bg-white/25 backdrop-blur-sm text-white font-semibold rounded-lg transition-all duration-200 text-base whitespace-nowrap border border-white/20"
            >
              {t('cta.buyItNow')}
              <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
            </a>
          </div>

          {/* Dropdown Category Selector */}
          <div className="max-w-md mx-auto animate-fade-in" style={{ animationDelay: '0.5s' }}>
            <label className="block text-white/70 text-sm font-medium mb-2">
              {t('dropdown.label')}
            </label>
            <div className="relative">
              <select
                value={selectedCategory}
                onChange={handleCategorySelect}
                className="w-full px-4 py-3 pr-10 rounded-lg bg-white/90 backdrop-blur-sm text-foreground-800 text-sm font-medium appearance-none cursor-pointer border-0 focus:ring-2 focus:ring-primary-400 outline-none"
              >
                <option value="">{t('dropdown.placeholder')}</option>
                {categoryList.map((cat) => (
                  <option key={cat.key} value={cat.url}>
                    {t(`categories.items.${cat.key}`)}
                  </option>
                ))}
              </select>
              <i className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-foreground-500 pointer-events-none" />
            </div>
          </div>
        </div>
      </section>

      {/* Categories Grid */}
      <section id="categories" className="py-16 md:py-24 px-4 md:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-4">
              {t('categories.h2')}
            </h2>
            <p className="text-foreground-600 max-w-2xl mx-auto text-sm md:text-base leading-relaxed">
              {t('categories.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-3 md:gap-4">
            {categoryList.map((cat) => (
              <a
                key={cat.key}
                href={cat.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex flex-col overflow-hidden rounded-xl bg-white border border-background-200 hover:border-primary-300 hover:shadow-md transition-all duration-300"
              >
                <div className="relative aspect-square overflow-hidden">
                  <img
                    src={cat.image}
                    alt={t(`categories.items.${cat.key}`)}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                  />
                </div>
                <div className="flex items-center justify-between gap-2 px-3 py-3 bg-white">
                  <span className="text-foreground-900 font-semibold text-sm leading-tight">
                    {t(`categories.items.${cat.key}`)}
                  </span>
                  <i className="ri-arrow-right-up-line w-4 h-4 flex items-center justify-center text-primary-500 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Products Showcase Section */}
      <section id="products" className="py-16 md:py-24 px-4 md:px-6 lg:px-8 bg-background-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-4">
              {t('products.title')}
            </h2>
            <p className="text-foreground-600 max-w-2xl mx-auto text-sm md:text-base leading-relaxed">
              {t('products.subtitle')}
            </p>
          </div>

          <div className="space-y-16 md:space-y-20">
            {productCategories.map((category) => (
              <div key={category.key}>
                <div className="flex items-center justify-between mb-6 md:mb-8">
                  <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">
                    {t(`categories.items.${category.key}`)}
                  </h3>
                  <a
                    href={category.categoryUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap"
                  >
                    {t('products.viewAll', { category: t(`categories.items.${category.key}`) })}
                    <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
                  </a>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
                  {category.products.map((product) => (
                    <a
                      key={product.id}
                      href={product.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group bg-white rounded-xl border border-background-200 overflow-hidden hover:border-primary-300 hover:shadow-md transition-all duration-300"
                      data-product-shop
                    >
                      <div className="relative aspect-square overflow-hidden bg-background-100">
                        <img
                          src={product.image}
                          alt={product.title}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                          loading="lazy"
                        />
                      </div>
                      <div className="p-3 md:p-4">
                        <h4 className="text-sm font-medium text-foreground-800 leading-snug line-clamp-2 mb-2 group-hover:text-primary-600 transition-colors">
                          {product.title}
                        </h4>
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-semibold text-primary-600">
                            {product.priceCNY}
                          </span>
                          <span className="text-xs text-foreground-500">
                            {product.priceUSD}
                          </span>
                        </div>
                        <div className="mt-3">
                          <span className="inline-flex items-center justify-center w-full px-3 py-2 bg-primary-500 hover:bg-primary-600 text-white text-xs font-medium rounded-md transition-colors">
                            {t('products.buyNow')}
                          </span>
                        </div>
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Guide Section */}
      <section id="guides" className="py-16 md:py-24 px-4 md:px-6 lg:px-8 bg-background-100">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-10 md:gap-16 items-center">
            <div className="order-2 lg:order-1">
              <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium mb-4">
                {t('nav.guides')}
              </span>
              <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-4">
                {t('guide.h2')}
              </h2>
              <p className="text-foreground-600 text-sm md:text-base leading-relaxed mb-6">
                {t('guide.text')}
              </p>
              <Link
                to={`/${langCode}/blog/cssbuy-spreadsheet-guide`}
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg transition-all duration-200 text-sm whitespace-nowrap"
              >
                {t('guide.cta')}
                <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
              </Link>
            </div>
            <div className="order-1 lg:order-2">
              <div className="relative rounded-2xl overflow-hidden aspect-[4/3]">
                <img
                  src="https://readdy.ai/api/search-image?query=Minimalist%20workspace%20with%20laptop%20showing%20spreadsheet%20and%20fashion%20items%20on%20desk%2C%20warm%20natural%20lighting%2C%20clean%20modern%20office%20aesthetic%2C%20neutral%20cream%20tones%2C%20soft%20shadows%2C%20editorial%20product%20photography%2C%20organized%20research%20setup&width=800&height=600&seq=guide-section-001&orientation=landscape"
                  alt="CSSBuy Spreadsheet Guide"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Review Section */}
      <section className="py-16 md:py-24 px-4 md:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-10 md:gap-16 items-center">
            <div className="relative rounded-2xl overflow-hidden aspect-[4/3]">
              <img
                src="https://readdy.ai/api/search-image?query=Premium%20shipping%20boxes%20and%20packages%20on%20a%20clean%20wooden%20table%2C%20logistics%20and%20delivery%20concept%2C%20warm%20natural%20lighting%2C%20minimal%20aesthetic%2C%20neutral%20cream%20tones%2C%20soft%20shadows%2C%20editorial%20product%20photography%2C%20modern%20warehouse%20style&width=800&height=600&seq=review-section-001&orientation=landscape"
                alt="CSSBuy Review"
                className="w-full h-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
            </div>
            <div>
              <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium mb-4">
                Review
              </span>
              <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-4">
                {t('review.h2')}
              </h2>
              <p className="text-foreground-600 text-sm md:text-base leading-relaxed mb-6">
                {t('review.text')}
              </p>
              <Link
                to={`/${langCode}/blog/cssbuy-review`}
                className="inline-flex items-center gap-2 px-6 py-3 bg-accent-500 hover:bg-accent-600 text-white font-medium rounded-lg transition-all duration-200 text-sm whitespace-nowrap"
              >
                {t('review.cta')}
                <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Shipping Section */}
      <section className="py-16 md:py-24 px-4 md:px-6 lg:px-8 bg-background-100">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-10 md:gap-16 items-center">
            <div className="order-2 lg:order-1">
              <span className="inline-block px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium mb-4">
                Shipping
              </span>
              <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-4">
                {t('shipping.h2')}
              </h2>
              <p className="text-foreground-600 text-sm md:text-base leading-relaxed mb-6">
                {t('shipping.text')}
              </p>
              <Link
                to={`/${langCode}/blog/cssbuy-shipping-cost`}
                className="inline-flex items-center gap-2 px-6 py-3 bg-secondary-500 hover:bg-secondary-600 text-white font-medium rounded-lg transition-all duration-200 text-sm whitespace-nowrap"
              >
                {t('shipping.cta')}
                <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
              </Link>
            </div>
            <div className="order-1 lg:order-2 relative rounded-2xl overflow-hidden aspect-[4/3]">
              <img
                src="https://readdy.ai/api/search-image?query=World%20map%20with%20shipping%20route%20lines%20and%20package%20icons%2C%20minimal%20logistics%20concept%20illustration%2C%20warm%20neutral%20tones%2C%20clean%20modern%20design%2C%20soft%20shadows%2C%20editorial%20style%2C%20cream%20and%20beige%20color%20palette%2C%20global%20delivery%20visualization&width=800&height=600&seq=shipping-section-001&orientation=landscape"
                alt="CSSBuy Shipping Cost"
                className="w-full h-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
            </div>
          </div>
        </div>
      </section>

      {/* Pillar Articles Section */}
      <section id="blog" className="py-16 md:py-24 px-4 md:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-4">
              {t('blog.h2')}
            </h2>
            <p className="text-foreground-600 max-w-2xl mx-auto text-sm md:text-base">
              {t('blog.subtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {articles.map((article) => {
              const content = article[langCode as 'en' | 'pt' | 'de'] || article.en;
              return (
                <article key={article.slug} className="group bg-white rounded-xl border border-background-200 overflow-hidden hover:shadow-md transition-all duration-300 flex flex-col">
                  <Link to={`/${langCode}/blog/${article.slug}`} className="block relative aspect-[16/10] overflow-hidden">
                    <img
                      src={article.image}
                      alt={content.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
                  </Link>
                  <div className="p-5 md:p-6 flex flex-col flex-1">
                    <span className="text-xs text-primary-600 font-medium">{content.date}</span>
                    <h3 className="font-heading text-lg font-semibold text-foreground-950 mt-2 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                      <Link to={`/${langCode}/blog/${article.slug}`}>{content.title}</Link>
                    </h3>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-4 line-clamp-3">
                      {content.excerpt}
                    </p>
                    <Link
                      to={`/${langCode}/blog/${article.slug}`}
                      className="mt-auto inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors"
                    >
                      {t('blog.readMore')}
                      <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
                    </Link>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-16 md:py-24 px-4 md:px-6 lg:px-8 bg-background-100">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-4">
              {t('faq.h2')}
            </h2>
          </div>
          <div className="space-y-4">
            {[
              { q: 'faq.q1', a: 'faq.a1' },
              { q: 'faq.q2', a: 'faq.a2' },
              { q: 'faq.q3', a: 'faq.a3' },
              { q: 'faq.q4', a: 'faq.a4' },
              { q: 'faq.q5', a: 'faq.a5' },
            ].map((faq, idx) => (
              <div key={idx} className="bg-white rounded-xl border border-background-200 p-5 md:p-6">
                <h3 className="font-heading text-base md:text-lg font-semibold text-foreground-900 mb-2">
                  {t(faq.q)}
                </h3>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                  {t(faq.a)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 md:py-24 px-4 md:px-6 lg:px-8 relative overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://readdy.ai/api/search-image?query=Soft%20abstract%20background%20with%20warm%20neutral%20tones%20and%20subtle%20gradient%2C%20cream%20and%20beige%20color%20palette%2C%20gentle%20light%20texture%2C%20minimal%20clean%20aesthetic%2C%20premium%20modern%20design%2C%20suitable%20for%20call-to-action%20section%20background&width=1600&height=800&seq=final-cta-bg-001&orientation=landscape"
            alt="Background"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/40" />
        </div>
        <div className="relative z-10 max-w-3xl mx-auto text-center">
          <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-4">
            {t('finalCta.h2')}
          </h2>
          <p className="text-white/85 text-sm md:text-base max-w-xl mx-auto mb-8 leading-relaxed">
            {t('finalCta.text')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 py-4 bg-primary-500 hover:bg-primary-600 text-white font-semibold rounded-lg transition-all duration-200 text-base whitespace-nowrap shadow-lg"
            >
              {t('finalCta.ctaPrimary')}
              <i className="ri-external-link-line w-4 h-4 flex items-center justify-center" />
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 py-4 bg-white/15 hover:bg-white/25 backdrop-blur-sm text-white font-semibold rounded-lg transition-all duration-200 text-base whitespace-nowrap border border-white/20"
            >
              {t('finalCta.ctaSecondary')}
              <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}